// Find buildings (prototype screen "Find buildings", PORTAL-WORKFLOW.md step 3): the anonymous
// shortlist of buildings in the company's Local Authority Districts that match its filters,
// from GET /v1/buildings (the validated snapshot). Before purchase it shows the broad sector,
// district and bands only: never a name, address or exact figure, and never where the data comes
// from (CLAUDE.md). Neil, 11 Oct 2026 ("make it so that they can't guess too much. We do all the
// important filtering after they've paid"): no floor area or electricity bands, no overdue or
// older-data flags; a card shows "Public building" ("Sector not shown") where too few share its
// sector, and payback and grid "Not shown" where too few share the whole card (the API decides).
//
// Two search bands on every row (Neil, 11 Oct 2026): opportunity (against every building we cover)
// and grid (spare grid capacity for the area), each with a minimum/only filter and the grid as a
// sort. Bands only; hover text says what each means without naming any source or method.
//
// Four more (Neil, 11 Oct 2026): existing solar (hidden by default: they're rarely worth a report),
// roof size band (minimum filter, largest-first sort), energy rating band (this rating or worse)
// and distance from the company's base postcode (maximum filter, nearest-first sort; without a
// base the filter area asks for one). Roof and rating show "Not shown" where the API hides them
// to keep a building anonymous; those rows only match the "Any" setting of their filters.
//
// A filter bar changes the list temporarily (districts, sizes, sectors, payback, data; overdue
// data is no longer a filter: showing or hiding those properties would say which they are):
// every option is shown with the saved coverage pre-ticked; districts can only narrow. They live in
// the URL, so the back button and shared links keep them. Coverage itself stays on its page.
//
// Private commercial (BUILD-PLAN-PRIVATE-COMMERCIAL.md PC5.1): an "All · Public · Private" choice at
// the top of the filters, with counts from facets.ownership, in the URL as ownership= and saved on
// the Filters page. Shown only where the API offers private properties (facets.ownership with
// private rows), so where they're off the page looks as it did. A generic card is "Public building"
// or "Business premises" (the API names it).
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Info, Map, RotateCcw } from "lucide-react";
import {
  useApi,
  type DistanceBand,
  type ElectricityData,
  type EnergyBand,
  type GridFilter,
  type OpportunityBand,
  type OwnershipFilter,
  type RoofBand,
  type Shortlist,
  type ShortlistFilters,
  type ShortlistSort,
} from "../api";
import { Segmented, Toggle } from "../components/controls";
import {
  DISTANCE_TIP,
  ENERGY_TIP,
  GRID_TIP,
  GlanceIcons,
  GlanceLegend,
  HIDDEN_TIP,
  OPPORTUNITY_TIP,
  OpportunityBadge,
  PaybackValue,
  ROOF_TIP,
  SECTOR_LABELS,
  SOLAR_TIP,
  genericType,
} from "../components/SearchBands";
import "../dashboard.css";
import "../coverage.css"; // chip styles
import "../find.css";

const SORTS: [ShortlistSort, string][] = [
  // The score is what the opportunity band ranks, so this is also "Top 10% first"
  ["score", "Best opportunity"],
  ["smallest", "Smallest first"],
  ["district", "District A–Z"],
  ["type", "Property type A–Z"],
  ["grid", "Grid capacity"],
  // By band, then best opportunity within each band (never by exact area or distance)
  ["roof", "Largest roof"],
  ["nearest", "Nearest first"], // offered only once a base postcode is set
];

// Roof filter: this band or larger (the API's keys, smallest first)
const ROOF_OPTIONS: [RoofBand | "any", string][] = [
  ["any", "Any roof size"],
  ["medium", "Medium or larger"],
  ["large", "Large or larger"],
  ["very-large", "Very large only"],
];

// Energy rating filter: this rating or worse (a poorly rated building usually uses more energy)
const RATING_OPTIONS: [EnergyBand | "any", string][] = [
  ["any", "Any energy rating"],
  ...(["C", "D", "E", "F"] as EnergyBand[]).map((k): [EnergyBand, string] => [k, `${k} or worse`]),
  ["G", "G only"],
];

// Distance filter: this band from the base postcode or nearer
const DISTANCE_OPTIONS: [Exclude<DistanceBand, "over-100"> | "any", string][] = [
  ["any", "Any distance"],
  ["within-10", "Within 10 miles"],
  ["10-25", "Within 25 miles"],
  ["25-50", "Within 50 miles"],
  ["50-100", "Within 100 miles"],
];

// Opportunity filter: this band or better (the API's band keys, best first)
const OPPORTUNITY_OPTIONS: [OpportunityBand | "any", string][] = [
  ["any", "Any opportunity"],
  ["top", "Top 10% only"],
  ["strong", "Strong or better"],
  ["good", "Good or better"],
];

// Grid filter: not saved with coverage, so "any" is the default
const GRID_OPTIONS: [GridFilter | "any", string][] = [
  ["any", "Any"],
  ["not-constrained", "Exclude constrained"],
  ["good", "Good grid capacity only"],
];

const PAYBACKS = [4, 5, 6, 7, 8, 10, 12];

/** "a,b" → ["a", "b"] */
const list = (v: string | null) => (v ? v.split(",").filter(Boolean) : []);

type FacetName = "districts" | "sizes" | "sectors";

/** URL parameters that change the view from the saved coverage (Reset clears them). */
const FILTER_PARAMS = ["ownership", "districts", "sizes", "sectors", "maxPayback", "data", "minOpportunity", "grid", "solar", "minRoof", "rating", "maxDistance"];

/** The URL value if it's one of the options (other than "any"), else null. */
function pickOption<K extends string>(options: [K | "any", string][], value: string | null): K | null {
  return options.map(([k]) => k).find((k): k is K => k !== "any" && k === value) ?? null;
}

// Public / Private: "any" (both) unless saved otherwise on the Filters page
const OWNERSHIP_KEYS: OwnershipFilter[] = ["any", "public", "private"];

const DATA_OPTIONS: [ElectricityData, string][] = [
  ["actual", "Actual only"],
  ["any", "Actual or estimated"],
];

export function FindBuildingsPage() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState<Shortlist | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Everything the view depends on comes from the URL
  const sort = (SORTS.some(([k]) => k === params.get("sort")) ? params.get("sort") : "score") as ShortlistSort;
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);
  // Unset parameters mean "as saved in coverage"
  const filters: ShortlistFilters = useMemo(
    () => ({
      districts: list(params.get("districts")),
      sizes: list(params.get("sizes")),
      sectors: list(params.get("sectors")),
      maxPayback: params.get("maxPayback") === "any" ? "any" : Number(params.get("maxPayback")) || null,
      data: (["actual", "any"] as const).find((d) => d === params.get("data")) ?? null,
      ownership: OWNERSHIP_KEYS.find((o) => o === params.get("ownership")) ?? null,
      minOpportunity: OPPORTUNITY_OPTIONS.map(([k]) => k).find((k): k is OpportunityBand => k !== "any" && k === params.get("minOpportunity")) ?? null,
      grid: GRID_OPTIONS.map(([k]) => k).find((k): k is GridFilter => k !== "any" && k === params.get("grid")) ?? null,
      // Mapped solar is a hint: properties are hidden only when asked (solar=hide)
      // Hidden by default (Neil, 14 Oct 2026); solar=any shows them
      hideSolar: params.get("solar") !== "any",
      minRoof: pickOption(ROOF_OPTIONS, params.get("minRoof")),
      rating: pickOption(RATING_OPTIONS, params.get("rating")),
      maxDistance: pickOption(DISTANCE_OPTIONS, params.get("maxDistance")),
    }),
    [params],
  );
  const filtered = FILTER_PARAMS.some((k) => params.has(k));

  /** Change URL parameters; any filter change goes back to page 1. */
  const update = (patch: Record<string, string | null>, keepPage = false) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!keepPage) next.delete("page");
    setParams(next, { replace: true });
  };
  // Every option is shown; the saved ones start ticked (no URL parameter = as saved; for
  // districts that's all of them, since they're fixed). Ticking more widens, unticking
  // narrows; getting back to the saved set clears the parameter; the last chip in a group
  // stays ticked, since an empty group would match nothing.
  const savedOf = (name: FacetName) => {
    const f = data?.facets;
    if (!f) return [];
    return name === "districts" ? f.districts.map((d) => d.key) : (f.saved?.[name] ?? []);
  };
  const selectedOf = (name: FacetName) => {
    const cur = list(params.get(name));
    return cur.length ? cur : savedOf(name);
  };
  const isOn = (name: FacetName, value: string) => selectedOf(name).includes(value);
  const toggleIn = (name: FacetName, value: string) => {
    const selected = selectedOf(name);
    const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
    if (next.length === 0) return;
    const order = (data?.facets?.[name] ?? []).map((f) => f.key).filter((k) => next.includes(k));
    const saved = savedOf(name);
    const same = order.length === saved.length && order.every((k) => saved.includes(k));
    update({ [name]: same ? null : order.join(",") });
  };

  useEffect(() => {
    setError(null);
    api
      .getShortlist(sort, page, filters)
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, [api, sort, page, filters]);

  // A building's preview, and how to come back to this exact view (filters, sort, page)
  const open = (ref: string) => `/find/${encodeURIComponent(ref)}`;
  const here = params.toString() ? `?${params}` : "";

  const pages = data?.pageSize ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const facets = data?.facets;
  // The control values: the URL's choice, else the saved one
  const savedPayback = facets?.saved?.maxPayback ?? null;
  const payback = filters.maxPayback ?? savedPayback ?? "any";
  const paybacks = [...new Set([...PAYBACKS, ...(savedPayback ? [savedPayback] : [])])].sort((a, b) => a - b);
  // "any" by default: estimated (private) properties would be hidden otherwise (PC4.2)
  const electricityData = filters.data ?? facets?.saved?.electricityData ?? "any";
  // Public / Private: the URL's choice, else the saved one; counts from the API's facet
  const savedOwnership = facets?.saved?.ownership ?? "any";
  const ownership = filters.ownership ?? savedOwnership;
  const owned = facets?.ownership;
  // Shown only where private properties are offered, or while a choice other than "All" is in force
  // (so it can always be put back, even if the counts then show no private rows)
  const showOwnership = Boolean(owned && (owned.private > 0 || ownership !== "any"));
  const ownershipOptions: [OwnershipFilter, string][] = owned
    ? [
        ["any", `All (${(owned.public + owned.private).toLocaleString("en-GB")})`],
        // "Public sector" / "Private sector": "Private" alone read as off-putting (Neil, 14 Oct 2026)
        ["public", `Public sector (${owned.public.toLocaleString("en-GB")})`],
        ["private", `Private sector (${owned.private.toLocaleString("en-GB")})`],
      ]
    : [];
  // The generic cards' names, for the hints: "Business premises" only where private ones can appear
  const genericNames = showOwnership ? `“${genericType("public")}” or “${genericType("private")}”` : `“${genericType("public")}”`;
  // What each search band means (the API's wording when it sends it)
  const opportunityTip = facets?.explain?.opportunity ?? OPPORTUNITY_TIP;
  const gridTip = facets?.explain?.grid ?? GRID_TIP;
  const solarTip = facets?.explain?.solar ?? SOLAR_TIP;
  const roofTip = facets?.explain?.roof ?? ROOF_TIP;
  const energyTip = facets?.explain?.energyRating ?? ENERGY_TIP;
  const distanceTip = facets?.explain?.distance ?? DISTANCE_TIP;
  const hiddenTip = facets?.explain?.hidden ?? HIDDEN_TIP;
  // Distance needs a base postcode: no nearest sort without one (the API ignores it anyway)
  const hasBase = Boolean(facets?.base?.postcode);
  const sortOptions = SORTS.filter(([k]) => k !== "nearest" || hasBase);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Search properties</h1>
        <p>
          Properties in your Local Authority Districts that use a lot of electricity and generate none of it. Names and addresses are shown once you order a
          report.
        </p>
      </div>

      {error && <div className="flash flash-danger">{error}</div>}

      {data && !data.coverageSet && (
        <div className="card find-empty">
          <h2>Set your coverage first</h2>
          <p className="card-sub">Choose the Local Authority Districts you work in and the properties you want. Your shortlist appears here straight away.</p>
          <Link to="/coverage" className="btn btn-primary">
            <Map size={16} aria-hidden="true" /> Set coverage
          </Link>
        </div>
      )}

      {data?.coverageSet && facets && (
        <>
          {/* Temporary filters: start from the saved coverage, for this view only */}
          <section className="card find-filters" aria-label="Filter the shortlist">
            {/* Public / Private first: it changes what every other filter is looking at */}
            {showOwnership && (
              <div className="find-filter-row">
                <span className="find-filter-label">Properties</span>
                <div className="find-filter-controls">
                  <Segmented
                    value={ownership}
                    options={ownershipOptions}
                    // Back to the saved choice clears the parameter
                    onChange={(o) => update({ ownership: o === savedOwnership ? null : o })}
                  />
                </div>
              </div>
            )}
            <div className="find-filter-row">
              <span className="find-filter-label">Districts</span>
              <div className="chip-row">
                {facets.districts.map((d) => (
                  <FilterChip key={d.key} on={isOn("districts", d.key)} label={d.label} onClick={() => toggleIn("districts", d.key)} />
                ))}
              </div>
            </div>
            <div className="find-filter-row">
              <span className="find-filter-label">System size</span>
              <div className="chip-row">
                {facets.sizes.map((s) => (
                  <FilterChip key={s.key} on={isOn("sizes", s.key)} label={s.label} onClick={() => toggleIn("sizes", s.key)} />
                ))}
              </div>
            </div>
            <div className="find-filter-row">
              <span className="find-filter-label">Sectors</span>
              <div className="chip-row">
                {facets.sectors.map((s) => (
                  <FilterChip key={s.key} on={isOn("sectors", s.key)} label={SECTOR_LABELS[s.key] ?? s.label} onClick={() => toggleIn("sectors", s.key)} />
                ))}
              </div>
            </div>
            {/* Generic cards (sector not shown) only match while every saved sector is ticked (the API's
                rule, so the filter can't reveal their sector): say so once a saved sector is unticked */}
            {savedOf("sectors").some((k) => !isOn("sectors", k)) && (
              <div className="find-filter-row">
                <span aria-hidden="true" />
                <p className="find-filter-hint muted">
                  Properties shown as {genericNames} (sector not shown) appear only when all your saved sectors are ticked.
                </p>
              </div>
            )}
            {/* Narrow down (Neil, 14 Oct 2026): Properties, Districts, System size and Sectors stay open; payback,
                electricity data and the optional extras each collapse, opening by themselves when in use
                (a saved payback or electricity data choice opens its section too) */}
            <div className="find-narrow">
              <div className="find-narrow-head">
                <span className="find-filter-label">Narrow down</span>
                <p className="find-filter-hint muted">Open any of these to narrow the list further. Changes here are for this view only.</p>
              </div>
              <NarrowItem label="Payback" value={payback === "any" ? "Any payback" : `Under ${payback} years`} active={payback !== "any"}>
              <div className="find-filter-row">
                <span className="find-filter-label">Payback</span>
                <div className="find-filter-controls">
                  <select
                    className="input find-payback"
                    value={String(payback)}
                    // Back to the saved value clears the parameter
                    onChange={(e) =>
                      update({
                        maxPayback: e.target.value === String(savedPayback ?? "any") ? null : e.target.value,
                      })
                    }
                  >
                    <option value="any">Any payback{savedPayback === null ? " (saved)" : ""}</option>
                    {paybacks.map((y) => (
                      <option key={y} value={y}>
                        Under {y} years{y === savedPayback ? " (saved)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              </NarrowItem>
              <NarrowItem label="Electricity data" value={DATA_OPTIONS.find(([k]) => k === electricityData)?.[1] ?? "Any"} active={electricityData !== "any"}>
              <div className="find-filter-row">
                <span className="find-filter-label">Electricity data</span>
                <div className="find-filter-controls">
                  <Segmented
                    value={electricityData}
                    options={DATA_OPTIONS}
                    onChange={(d) =>
                      update({
                        data: d === facets.saved?.electricityData ? null : d,
                      })
                    }
                  />
                </div>
              </div>
              </NarrowItem>
              <NarrowItem label="Opportunity" tip={opportunityTip} value={optionLabel(OPPORTUNITY_OPTIONS, filters.minOpportunity)} active={!!filters.minOpportunity}>
              <div className="find-filter-row">
                <span className="find-filter-label find-th-help" title={opportunityTip}>
                  Opportunity <Info size={12} aria-hidden="true" />
                </span>
                <div className="find-filter-controls">
                  <select
                    className="input find-opportunity"
                    aria-label="Opportunity"
                    title={opportunityTip}
                    value={filters.minOpportunity ?? "any"}
                    onChange={(e) => update({ minOpportunity: e.target.value === "any" ? null : e.target.value })}
                  >
                    {OPPORTUNITY_OPTIONS.map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              </NarrowItem>
              <NarrowItem label="Grid" tip={gridTip} value={optionLabel(GRID_OPTIONS, filters.grid)} active={!!filters.grid}>
              <div className="find-filter-row">
                <span className="find-filter-label find-th-help" title={gridTip}>
                  Grid <Info size={12} aria-hidden="true" />
                </span>
                <div className="find-filter-controls">
                  <Segmented value={filters.grid ?? "any"} options={GRID_OPTIONS} onChange={(g) => update({ grid: g === "any" ? null : g })} />
                </div>
              </div>
              </NarrowItem>
              <NarrowItem label="Roof size" tip={roofTip} value={optionLabel(ROOF_OPTIONS, filters.minRoof)} active={!!filters.minRoof}>
              <BandSelect label="Roof size" tip={roofTip} param="minRoof" value={filters.minRoof} options={ROOF_OPTIONS} update={update} />
              </NarrowItem>
              <NarrowItem label="Energy rating" tip={energyTip} value={optionLabel(RATING_OPTIONS, filters.rating)} active={!!filters.rating}>
              <BandSelect label="Energy rating" tip={energyTip} param="rating" value={filters.rating} options={RATING_OPTIONS} update={update} />
              </NarrowItem>
              <NarrowItem label="Distance" tip={distanceTip} value={hasBase ? optionLabel(DISTANCE_OPTIONS, filters.maxDistance) : "Set your base"} active={!!filters.maxDistance}>
              {hasBase ? (
                <BandSelect
                  label="Distance"
                  tip={`${distanceTip} Your base: ${facets.base?.postcode}.`}
                  param="maxDistance"
                  value={filters.maxDistance}
                  options={DISTANCE_OPTIONS}
                  update={update}
                />
              ) : (
                <div className="find-filter-row">
                  <span className="find-filter-label find-th-help" title={distanceTip}>
                    Distance <Info size={12} aria-hidden="true" />
                  </span>
                  <p className="find-base-prompt muted">
                    <Link to="/profile">Set your base postcode</Link> to see how far each property is from you.
                  </p>
                </div>
              )}
              </NarrowItem>
              <NarrowItem label="Solar already fitted" value={filters.hideSolar ? "Hidden" : "Shown"} active={!filters.hideSolar}>
              <div className="find-filter-row">
                <span aria-hidden="true" />
                {/* On by default (Neil, 14 Oct 2026): properties where solar may already be fitted are hidden unless asked for; our roof check still decides after purchase */}
                <Toggle
                  id="find-solar"
                  checked={!!filters.hideSolar}
                  label="Hide properties where solar may already be fitted"
                  // Ticked is the default (no parameter); unticked asks for them (solar=any)
                  onChange={(on) => update({ solar: on ? null : "any" })}
                />
              </div>
              </NarrowItem>
            </div>
            <div className="find-filter-foot">
              <p className="find-filter-hint muted">
                Ticked options are your saved coverage and filters. Changes here are for this view only. To change them, go to{" "}
                <Link to="/coverage">Coverage</Link> or <Link to="/filters">Filters</Link>.
              </p>
              {filtered && (
                <button
                  type="button"
                  className="btn btn-secondary find-reset"
                  onClick={() => setParams(new URLSearchParams(sort !== "score" ? { sort } : {}), { replace: true })}
                >
                  <RotateCcw size={14} aria-hidden="true" /> Reset
                </button>
              )}
            </div>
          </section>

          <div className="find-bar">
            <p className="find-total">
              <b>{data.total.toLocaleString("en-GB")}</b> {data.total === 1 ? "property matches" : "properties match"}
              {filtered ? " these filters" : " your coverage"}
              {data.generatedAt && (
                <span className="muted">
                  {" "}
                  · updated{" "}
                  {new Date(data.generatedAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              )}
            </p>
            <Segmented
              value={sortOptions.some(([k]) => k === sort) ? sort : "score"}
              options={sortOptions}
              onChange={(s) => update({ sort: s === "score" ? null : s })}
            />
          </div>

          {data.total === 0 ? (
            <div className="card find-empty">
              <p className="card-sub">
                {filtered
                  ? "Nothing matches these filters. Try fewer, or reset them."
                  : "Nothing matches yet. Try more system sizes or sectors, or a longer payback, in Filters."}
              </p>
            </div>
          ) : (
            <div className="card find-table-card">
              {/* What the icons in these results mean (only those that appear) */}
              <GlanceLegend rows={data.buildings} />
              <div className="table-scroll">
                <table className="stats-table find-table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>
                        <span className="find-th-help" title={opportunityTip}>
                          Opportunity <Info size={12} aria-hidden="true" />
                        </span>
                      </th>
                      <th>At a glance</th>
                      <th>Indicative system</th>
                      <th>Payback</th>
                      <th>Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.buildings.map((b) => (
                      // The whole row opens the preview; the type is also a link for keyboards
                      <tr key={b.ref} className="find-row" onClick={() => navigate(open(b.ref), { state: { from: here } })}>
                        <td>
                          <Link to={open(b.ref)} state={{ from: here }} className="find-type find-row-link" onClick={(e) => e.stopPropagation()}>
                            {b.buildingType}
                          </Link>
                          {/* The district and, once a base is set, the distance band (the type above is the sector; a hidden
                              sector shows as the eye icon) */}
                          <div className="muted find-sub">
                            {b.district}
                            {b.distance && b.distance.band !== "not-known" ? ` · ${b.distance.label}` : ""}
                          </div>
                        </td>
                        <td>
                          <OpportunityBadge value={b.opportunity} tip={opportunityTip} />
                        </td>
                        <td>
                          <GlanceIcons b={b} tips={{ grid: gridTip, roof: roofTip, energy: energyTip, solar: solarTip, hidden: hiddenTip }} />
                        </td>
                        <td>{b.sizeLabel}</td>
                        <td>
                          <PaybackValue value={b.payback} hiddenTip={hiddenTip} />
                        </td>
                        <td className="find-credits">{b.credits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="find-pager">
                <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => update({ page: String(page - 1) }, true)}>
                  <ChevronLeft size={16} aria-hidden="true" /> Previous
                </button>
                <span className="muted">
                  Page {page} of {pages.toLocaleString("en-GB")}
                </span>
                <button type="button" className="btn btn-secondary" disabled={page >= pages} onClick={() => update({ page: String(page + 1) }, true)}>
                  Next <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
          <p className="find-note muted">
            Payback and system size are indicative, from electricity use alone; the report replaces them with a roof scan and a full costing. Grid
            capacity is a guide for the area around the property, not a connection offer. Payback, grid, roof size and energy rating show "Not
            shown", and a property shows as {genericNames}, where showing more could identify it.
          </p>
        </>
      )}
    </div>
  );
}

/** One band filter row: a label with a "what this means" hover and a dropdown ("any" clears it). */
/** The label of the current choice in an options list ("any" when nothing is chosen). */
function optionLabel<K extends string>(options: [K | "any", string][], value: K | null | undefined): string {
  return (options.find(([k]) => k === (value ?? "any")) ?? options[0])[1];
}

/**
 * One optional narrowing filter: collapsed, its name and current choice on the summary line, open
 * by itself while it's in use so an applied filter is never hidden.
 */
function NarrowItem({ label, tip, value, active, children }: { label: string; tip?: string; value: string; active: boolean; children: ReactNode }) {
  return (
    <details className={`find-narrow-item${active ? " is-active" : ""}`} open={active || undefined}>
      <summary>
        {/* The filter's explanation, on the summary line (the row's own label is hidden inside) */}
        <span className={`find-narrow-label${tip ? " find-th-help" : ""}`} title={tip}>
          {label} {tip && <Info size={12} aria-hidden="true" />}
        </span>
        <span className="find-narrow-value">{value}</span>
      </summary>
      {children}
    </details>
  );
}

function BandSelect<K extends string>({
  label,
  tip,
  param,
  value,
  options,
  update,
}: {
  label: string;
  tip: string;
  param: string;
  value: K | null | undefined;
  options: [K | "any", string][];
  update: (patch: Record<string, string | null>) => void;
}) {
  return (
    <div className="find-filter-row">
      <span className="find-filter-label find-th-help" title={tip}>
        {label} <Info size={12} aria-hidden="true" />
      </span>
      <div className="find-filter-controls">
        <select
          className="input find-band-select"
          aria-label={label}
          title={tip}
          value={value ?? "any"}
          onChange={(e) => update({ [param]: e.target.value === "any" ? null : e.target.value })}
        >
          {options.map(([k, text]) => (
            <option key={k} value={k}>
              {text}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

/** A filter pill (aria-pressed), as on Coverage & filters. */
function FilterChip({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`chip${on ? " is-on" : ""}`} aria-pressed={on} onClick={onClick}>
      {on && <Check size={12} aria-hidden="true" />} {label}
    </button>
  );
}
